import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Typography } from "@mui/material";
import React, { useCallback, useState } from "react";
import * as XLSX from "xlsx";
import DownloadCSV from "./DownloadCSV";
import { reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { FILE_UPLOAD_ERROR_MESSAGE } from 'src/constants/messages';
import { UploadDropArea, VisuallyHiddenInput } from 'src/UIKit';

type UploadScreenProps = {
  onFileSelected: (file: File, headers: string[]) => void;
  setIsUploading: (isUploading: boolean) => void;
  setUploadProgress: (progress: number) => void;
};

export default function UploadScreen({ onFileSelected, setIsUploading, setUploadProgress }: UploadScreenProps) {
  const [isDragOver, setIsDragOver] = useState(false);

  const formatHeaders = () => {
    const fields = "Phone number,First name,Last name,Email,Birthdate,Timezone,";
    return fields.replace(/,\s*$/, "");
  };

  const readCsvHeaders = async (selectedFile: File): Promise<string[]> => {
    const text = await selectedFile.text();
    const sanitized = text.replace(/^\uFEFF/, ""); // remove BOM if present
    const firstLine = sanitized.split(/\r?\n/)[0] ?? "";
    const result: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < firstLine.length; i++) {
      const char = firstLine[i];
      if (char === '"') {
        if (inQuotes && firstLine[i + 1] === '"') {
          current += '"';
          i++; // skip escaped quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const readXlsxHeaders = async (selectedFile: File): Promise<string[]> => {
    const data = await selectedFile.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) return [];
    const sheet = workbook.Sheets[firstSheetName];
    const rows = XLSX.utils.sheet_to_json<string[][]>(sheet, { header: 1 });
    const headerRow = (rows[0] || []) as unknown as (string | number | null | undefined)[];
    // Normalize to strings and trim, ensure we capture all columns even if empty
    const headers = headerRow.map((h, idx) => {
      const val = h != null ? String(h).trim() : "";
      return val || `Column ${idx + 1}`;
    });
    return headers;
  };

  const processFile = async (file: File) => {
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      setUploadProgress(25);
      
      const lowerName = file.name.toLowerCase();
      const isCsv = lowerName.endsWith(".csv");
      const isExcel = lowerName.endsWith(".xlsx") || lowerName.endsWith(".xls");
      
      setUploadProgress(50);
      
      const headers = isCsv
        ? await readCsvHeaders(file)
        : isExcel
        ? await readXlsxHeaders(file)
        : await readCsvHeaders(file);

      setUploadProgress(75);
      
      onFileSelected(file, headers);
      setUploadProgress(100);
    } catch (e) {
      reportErrorToUser(e, FILE_UPLOAD_ERROR_MESSAGE);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target?.files?.[0];
      if (file) {
        processFile(file);
      }
    },
    []
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);

    const files = Array.from(event.dataTransfer.files);
    const file = files[0];
    if (file) {
      const lowerName = file.name.toLowerCase();
      if (lowerName.endsWith(".csv") || lowerName.endsWith(".xlsx") || lowerName.endsWith(".xls")) {
        processFile(file);
      }
    }
  }, []);

  return (
    <>
      <Typography variant="body1" textAlign="center" mb={2.5}>
        Upload your contacts from a CSV or Excel file. All contacts must have a phone number to be uploaded. Use your own file or start with {" "}
        <DownloadCSV data={formatHeaders()} fileName="Contact-CSV-template" />.
      </Typography>
      <UploadDropArea
        sx={{ width: '85%' }}
        className={isDragOver ? "dragover" : ""}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        component="label"
      >
        <CloudUploadIcon sx={{ mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Drag and drop your file here
        </Typography>
        <Typography variant="body2" color="text.secondary">
          or click to browse
        </Typography>
        <VisuallyHiddenInput
          type="file"
          onChange={handleFileUpload}
          accept=".csv,.xlsx,.xls"
        />
      </UploadDropArea>
    </>
  );
}
